import type { ReactNode } from "react";
import { platformLabel } from "./platforms.js";
import styles from "./PlatformBadge.module.scss";

/** Brand glyphs (24×24, single-path, `fill=currentColor`) ported byte-faithful from the cd-dashboard mockup. */
const GLYPHS: Record<string, ReactNode> = {
  spotify: (
    <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.3a.75.75 0 0 1-1.03.25c-2.82-1.72-6.36-2.11-10.54-1.16a.75.75 0 1 1-.33-1.46c4.57-1.04 8.49-.59 11.65 1.34.36.22.47.68.25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 1 1-.54-1.8c4.36-1.32 9.78-.68 13.49 1.6.44.27.58.85.31 1.29zm.13-3.4C15.73 8.26 9.1 8.03 5.39 9.16a1.12 1.12 0 1 1-.65-2.15c4.26-1.29 11.58-1.04 16.14 1.66a1.12 1.12 0 1 1-1.15 1.93z" />
  ),
  instagram: (
    <>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.43-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.12 1.38C1.36 2.67.94 3.34.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.8.72 1.47 1.38 2.13.66.66 1.33 1.08 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.7 5.7 0 0 0 2.13-1.38 5.7 5.7 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.7 5.7 0 0 0-1.38-2.12A5.7 5.7 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0z" />
      <path d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84M12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4z" />
      <circle cx="18.41" cy="5.59" r="1.44" />
    </>
  ),
  facebook: (
    <path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z" />
  ),
  youtube: (
    <path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.55 15.57V8.43L15.82 12z" />
  ),
  tiktok: (
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.08-2.82h-3.2v12.93a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 1 1 .73-5.07V9.9a5.82 5.82 0 0 0-.73-.05A5.79 5.79 0 1 0 15.5 15.6V9.04a7.5 7.5 0 0 0 4.5 1.5V7.36a4.28 4.28 0 0 1-3.4-1.54z" />
  ),
};

/** Box rungs: `sm` 24px (the analytics tiles), `md` 44px (a Trending pill's thumb), `lg` 56px (its detail
 *  head). Sizes rather than free pixels so the glyph scales with the tile on the same ladder. */
export type PlatformBadgeSize = "sm" | "md" | "lg";

/**
 * The platform badge — a brand-coloured tile with a white glyph. The fill comes from the `--ac-color-p-*`
 * platform tokens via `data-platform` (SCSS), so no colour is hard-coded. A platform without a bundled glyph
 * degrades to its initial on a neutral tile — never a broken/absent badge.
 *
 * It is BOTH lower rungs of the Trending spec's thumb chain (real artwork → platform SVG icon → letter tile):
 * the glyph is rung two and its own initial fallback is rung three, so a caller only has to supply the
 * artwork rung above it.
 *
 * `shape` is independent of `size` on purpose — a Trending pill's thumb is a 44px CIRCLE and its detail head
 * a 56px rounded square, and neither is derivable from the other.
 */
export function PlatformBadge({
  platform,
  size = "sm",
  shape = "square",
}: {
  platform: string | null | undefined;
  size?: PlatformBadgeSize;
  shape?: "square" | "circle";
}) {
  const key = platform ?? "";
  const glyph = GLYPHS[key];
  return (
    <span
      className={styles.badge}
      data-platform={key || undefined}
      data-size={size}
      data-shape={shape}
      aria-hidden="true"
    >
      {glyph ? (
        <svg viewBox="0 0 24 24" fill="currentColor" className={styles.glyph}>
          {glyph}
        </svg>
      ) : (
        <span className={styles.initial}>{(platformLabel(platform)[0] ?? "?").toUpperCase()}</span>
      )}
    </span>
  );
}
